/* eslint-disable no-await-in-loop */
import { getContentLanguage } from "shared/lib/locale/contentLanguage"
import { ClientError } from "shared/types/error/ClientError"
import { FetchError } from "shared/types/error/FetchError"
import { NetworkError } from "shared/types/error/NetworkError"
import { NotFoundError } from "shared/types/error/NotFoundError"
import { RequestError } from "shared/types/error/RequestError"
import { ServerError } from "shared/types/error/ServerError"
import { TimeoutError } from "shared/types/error/TimeoutError"
import { TooManyRequestsError } from "shared/types/error/TooManyRequestsError"
import { UnauthorizedError } from "shared/types/error/UnauthorizedError"

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

async function request(
  path: RequestInfo | URL,
  init: RequestInit,
  args?: {
    ignoredStatuses?: number[]
    getToken?: () => Promise<string | null> | string | null | undefined
    getFreshToken?: () => Promise<string | null>
  }
) {
  const { getToken, getFreshToken } = args || {}

  const token = getToken ? await getToken() : null
  const headers = new Headers(init.headers || {})
  headers.set("Accept-Language", getContentLanguage())
  if (token) {
    headers.set("Authorization", `Bearer ${token}`)
  }
  const derivedInit = { ...init, headers }

  let response: Response
  try {
    response = await fetch(path, derivedInit)
  } catch (cause) {
    throw new FetchError({ path, init, response: null, cause })
  }

  if (!response.ok) {
    const { ignoredStatuses = [] } = args || {}
    if (ignoredStatuses.includes(response.status)) {
      return response
    }
    let message: string | undefined
    try {
      message = (await response.clone().json()).message
    } catch {
      // Do nothing
    }
    const errorArgs = { path, init, message, response: response.clone() }
    if (response.status === 401 || response.status === 403) {
      if (getFreshToken) {
        // Try once more with a fresh token
        const freshToken = await getFreshToken()
        return request(path, derivedInit, {
          getToken: () => freshToken
        })
      }
      throw new UnauthorizedError(errorArgs)
    } else if (response.status === 404) {
      throw new NotFoundError(errorArgs)
    } else if (response.status === 429) {
      throw new TooManyRequestsError(errorArgs)
    } else if (response.status >= 400 && response.status < 500) {
      throw new ClientError(errorArgs)
    } else if (response.status >= 500) {
      throw new ServerError(errorArgs)
    } else {
      throw new RequestError(errorArgs)
    }
  }
  return response
}

function getNextDelay(args: {
  attempt: number
  initialDelay: number
  retryDelay: number
  maxDelay: number
  exponential: boolean
}) {
  const { attempt, initialDelay, retryDelay, maxDelay, exponential } = args
  if (!exponential) {
    return retryDelay
  }
  return Math.min(initialDelay * 2 ** attempt, maxDelay)
}

export async function fetchWithRetries(
  path: RequestInfo | URL,
  init: RequestInit = {},
  options?: {
    maxRetries?: number
    initialDelay?: number
    retryDelay?: number
    maxDelay?: number
    exponential?: boolean
    timeout?: number | null
    getToken?: () => Promise<string | null> | string | null | undefined
    getFreshToken?: () => Promise<string | null>
  }
): Promise<Response> {
  const {
    maxRetries = null,
    retryDelay = 1000,
    exponential = true,
    maxDelay = 10000,
    initialDelay = 50,
    timeout = null,
    getToken,
    getFreshToken
  } = options || {}
  const { method = "GET" } = init || {}

  let operationTimeout: NodeJS.Timeout | number | null = null

  function clearOperationTimeout() {
    if (operationTimeout) {
      clearTimeout(operationTimeout)
    }
  }

  if (method.toUpperCase() !== "GET" || maxRetries === 0) {
    const abortController = new AbortController()
    const { signal } = abortController
    const response = request(
      path,
      { signal, ...init },
      {
        // The resource is already gone, e.g. after a duplicate submit
        ignoredStatuses: method.toUpperCase() === "DELETE" ? [404] : [],
        getToken,
        getFreshToken
      }
    )
    if (timeout) {
      operationTimeout = setTimeout(() => abortController.abort(), timeout || 0)
    }
    try {
      return await response
    } finally {
      clearOperationTimeout()
    }
  }

  const maxAttempts = maxRetries !== null ? maxRetries + 1 : Infinity
  let attempts = 0
  let currentDelay = exponential ? initialDelay : retryDelay
  let fatalError: Error | null = null
  let retryableError: ServerError | NetworkError | null = null

  const abortController = new AbortController()

  if (timeout) {
    operationTimeout = setTimeout(() => {
      abortController.abort(`request was aborted after ${timeout} ms`)
    }, timeout)
  }

  try {
    while (
      attempts < maxAttempts &&
      !fatalError &&
      !abortController.signal.aborted
    ) {
      currentDelay = getNextDelay({
        attempt: attempts,
        initialDelay,
        retryDelay,
        maxDelay,
        exponential
      })

      // Add some jitter (+/- 10% of the delay) to avoid thundering herd
      const delayWithJitter =
        currentDelay + (Math.random() * 2 - 1) * 0.1 * currentDelay

      // Wait before retry (except for first attempt)
      if (attempts > 0) {
        await sleep(delayWithJitter)
      }

      const derivedInit = {
        signal: abortController.signal,
        ...init
      }

      try {
        const response = await request(path, derivedInit, {
          ignoredStatuses: [404],
          getToken,
          getFreshToken: attempts === 0 ? getFreshToken : undefined
        })

        clearOperationTimeout()
        return response
      } catch (err) {
        if (err instanceof ServerError || err instanceof NetworkError) {
          retryableError = err
        } else if (err instanceof Error) {
          fatalError = err
        }
        attempts += 1
      }
    }
    if (fatalError?.name === "AbortError") {
      throw (
        retryableError ||
        new TimeoutError({
          path,
          init,
          message: fatalError.message
        })
      )
    }
    throw (
      fatalError ||
      retryableError ||
      new RequestError({ path, init, response: null })
    )
  } finally {
    clearOperationTimeout()
  }
}
